import { useState } from "react";
import { useAdminStats, useAdminUsers, useAdminModels, useAdminUpdateModel } from "../hooks/useApi";
import Card from "../components/Card";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import Button from "../components/Button";
import GlassModal from "../components/GlassModal";
import { formatNumber, formatTomanInt } from "../utils/format";

export default function AdminDashboard() {
  const [tab, setTab] = useState<"overview" | "users" | "models">("overview");
  const { data: stats } = useAdminStats();
  const { data: users, isLoading: usersLoading } = useAdminUsers();
  const { data: models, isLoading: modelsLoading } = useAdminModels();
  const updateModel = useAdminUpdateModel();

  const [editModel, setEditModel] = useState<Record<string, unknown> | null>(null);
  const [editInputPrice, setEditInputPrice] = useState("");
  const [editOutputPrice, setEditOutputPrice] = useState("");

  const statusBadge = (status: string) => {
    switch (status) {
      case "active": return <Badge variant="success">فعال</Badge>;
      case "inactive": return <Badge variant="warning">غیرفعال</Badge>;
      case "deprecated": return <Badge variant="error">منسوخ</Badge>;
      default: return <Badge variant="info">{status}</Badge>;
    }
  };

  const userColumns = [
    { key: "email", header: "ایمیل" },
    { key: "name", header: "نام", render: (item: Record<string, unknown>) => <span>{item.name as string || "-"}</span> },
    { key: "status", header: "وضعیت", render: (item: Record<string, unknown>) => statusBadge(item.status as string) },
    { key: "is_admin", header: "مدیر", render: (item: Record<string, unknown>) => item.is_admin ? <Badge variant="info">بله</Badge> : "-" },
    { key: "created_at", header: "تاریخ ثبت‌نام", render: (item: Record<string, unknown>) => <span className="text-small text-text-tertiary">{new Date(item.created_at as string).toLocaleDateString("fa-IR")}</span> },
  ];

  const modelColumns = [
    { key: "model_name", header: "مدل", render: (item: Record<string, unknown>) => <span className="font-number text-small ltr text-left block">{item.model_name as string}</span> },
    { key: "provider", header: "ارائه‌دهنده", render: (item: Record<string, unknown>) => <span className="text-small text-text-secondary">{item.provider as string}</span> },
    {
      key: "input_price",
      header: "قیمت ورودی",
      render: (item: Record<string, unknown>) => {
        const price = parseFloat(String(item.input_price));
        const inToman = price / 10;
        return <span className="font-number text-small">{inToman.toFixed(inToman < 1 ? 6 : 0)}</span>;
      },
    },
    {
      key: "output_price",
      header: "قیمت خروجی",
      render: (item: Record<string, unknown>) => {
        const price = parseFloat(String(item.output_price));
        const inToman = price / 10;
        return <span className="font-number text-small">{inToman.toFixed(inToman < 1 ? 6 : 0)}</span>;
      },
    },
    { key: "unit_type", header: "واحد", render: (item: Record<string, unknown>) => <span className="text-small text-text-tertiary">{item.unit_type as string}</span> },
    { key: "status", header: "وضعیت", render: (item: Record<string, unknown>) => statusBadge(item.status as string) },
    {
      key: "actions",
      header: "عملیات",
      render: (item: Record<string, unknown>) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditModel(item);
            setEditInputPrice(String(item.input_price));
            setEditOutputPrice(String(item.output_price));
          }}
        >
          ویرایش
        </Button>
      ),
    },
  ];

  const tabs = [
    { key: "overview", label: "خلاصه" },
    { key: "users", label: "کاربران" },
    { key: "models", label: "مدل‌ها" },
  ] as const;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h1 text-text-main">پنل مدیریت</h1>
        <p className="text-body text-text-secondary mt-1">مدیریت سیستم و نظارت</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-hover rounded-btn p-1 w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-btn text-small font-medium transition-all ${
              tab === t.key ? "bg-white text-text-main shadow-sm" : "text-text-secondary hover:text-text-main"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <Card>
              <p className="text-small text-text-tertiary mb-1">کل کاربران</p>
              <p className="text-h1 text-text-main font-number">{formatNumber(stats?.total_users)}</p>
            </Card>
            <Card>
              <p className="text-small text-text-tertiary mb-1">کاربران فعال</p>
              <p className="text-h1 text-text-main font-number">{formatNumber(stats?.active_users)}</p>
            </Card>
            <Card>
              <p className="text-small text-text-tertiary mb-1">مجموع درخواست‌ها</p>
              <p className="text-h1 text-text-main font-number">{formatNumber(stats?.total_requests)}</p>
            </Card>
            <Card>
              <p className="text-small text-text-tertiary mb-1">درآمد کل</p>
              <p className="text-h1 text-text-main font-number">
                {formatTomanInt(stats?.total_revenue)}
                <span className="text-body text-text-tertiary font-normal mr-1">تومان</span>
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card padding="md">
              <p className="text-small text-text-tertiary mb-1">مدل‌های فعال</p>
              <p className="text-h1 text-text-main font-number">{formatNumber(stats?.active_models_count)}</p>
            </Card>
            <Card padding="md">
              <p className="text-small text-text-tertiary mb-1">وضعیت سیستم</p>
              <div className="flex items-center gap-2 mt-2">
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                <span className="text-body text-text-main">فعال</span>
              </div>
            </Card>
          </div>
        </>
      )}

      {tab === "users" && (
        <Card padding="md">
          <h2 className="text-h2 text-text-main mb-4">کاربران</h2>
          <DataTable
            columns={userColumns}
            data={(users || []) as Record<string, unknown>[]}
            loading={usersLoading}
            emptyMessage="هیچ کاربری یافت نشد"
          />
        </Card>
      )}

      {tab === "models" && (
        <Card padding="md">
          <h2 className="text-h2 text-text-main mb-4">مدل‌های AI</h2>
          <DataTable
            columns={modelColumns}
            data={(models || []) as Record<string, unknown>[]}
            loading={modelsLoading}
            emptyMessage="هیچ مدلی ثبت نشده است"
          />
        </Card>
      )}

      {/* Edit Model Modal */}
      <GlassModal
        open={!!editModel}
        onClose={() => setEditModel(null)}
        title={`ویرایش قیمت: ${(editModel?.model_name as string) || ""}`}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">قیمت ورودی (IRR)</label>
            <input
              type="text"
              value={editInputPrice}
              onChange={(e) => setEditInputPrice(e.target.value.replace(/[^0-9.]/g, ""))}
              className="w-full h-10 px-3.5 rounded-btn border border-[#E2E8F0] text-body font-number text-left focus:outline-none focus:ring-2 focus:ring-primary/30"
              dir="ltr"
            />
          </div>
          <div>
            <label className="block text-small font-medium text-text-secondary mb-1.5">قیمت خروجی (IRR)</label>
            <input
              type="text"
              value={editOutputPrice}
              onChange={(e) => setEditOutputPrice(e.target.value.replace(/[^0-9.]/g, ""))}
              className="w-full h-10 px-3.5 rounded-btn border border-[#E2E8F0] text-body font-number text-left focus:outline-none focus:ring-2 focus:ring-primary/30"
              dir="ltr"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setEditModel(null)}>انصراف</Button>
            <Button
              className="flex-1"
              onClick={() => {
                if (editModel) {
                  updateModel.mutate({
                    id: String(editModel.id),
                    data: { input_price: parseFloat(editInputPrice), output_price: parseFloat(editOutputPrice) },
                  });
                  setEditModel(null);
                }
              }}
              loading={updateModel.isPending}
            >
              ذخیره
            </Button>
          </div>
        </div>
      </GlassModal>
    </div>
  );
}